import styled from '@emotion/styled';
import type { InputSize, InputVariant } from './types';

type FieldProps = {
  $variant: InputVariant;
  $size: InputSize;
  $hasError?: boolean;
  $fullWidth?: boolean;
  $hasLeftIcon?: boolean;
  $hasRightIcon?: boolean;
};

function getVariantTokens(theme: any, variant: InputVariant) {
  return theme?.component?.input?.[variant] ?? theme?.component?.input?.default ?? {};
}

function getSizeTokens(theme: any, size: InputSize) {
  return theme?.component?.input?.size?.[size] ?? theme?.component?.input?.size?.md ?? {};
}

export const InputWrapper = styled.div<{ $fullWidth?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  box-sizing: border-box;
`;

export const IconSlot = styled.span<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $side }) => ($side === 'left' ? 'left: 12px;' : 'right: 12px;')}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme?.colors?.text?.secondary ?? '#808080'};
  pointer-events: none;
  z-index: 1;

  svg {
    width: 1em;
    height: 1em;
  }
`;

const fieldBase = (props: {
  theme?: any;
  $variant: InputVariant;
  $size: InputSize;
  $hasError?: boolean;
  $fullWidth?: boolean;
  $hasLeftIcon?: boolean;
  $hasRightIcon?: boolean;
}) => {
  const { theme, $variant, $size, $hasError, $fullWidth, $hasLeftIcon, $hasRightIcon } = props;
  const tokens = getVariantTokens(theme, $variant);
  const sizeTokens = getSizeTokens(theme, $size);
  const borderWidth =
    $variant === 'search' ? tokens.borderWidth ?? '2px' : theme?.component?.input?.borderWidth ?? '1px';
  const borderColor = $hasError
    ? tokens.errorBorderColor ?? '#d32f2f'
    : tokens.borderColor ?? theme?.colors?.border?.color;

  return `
    box-sizing: border-box;
    width: ${$fullWidth ? '100%' : 'auto'};
    font-family: inherit;
    font-size: ${sizeTokens.fontSize ?? '1rem'};
    padding: ${sizeTokens.padding ?? '1rem'};
    ${$hasLeftIcon ? 'padding-left: 2.75rem;' : ''}
    ${$hasRightIcon ? 'padding-right: 2.75rem;' : ''}
    min-height: ${sizeTokens.minHeight ?? '48px'};
    background-color: ${tokens.backgroundColor ?? '#ffffff'};
    color: ${tokens.color ?? theme?.colors?.text?.primary ?? '#292929'};
    border: ${$variant === 'borderless' ? 'none' : `${borderWidth} solid ${borderColor}`};
    border-radius: ${theme?.component?.input?.borderRadius ?? theme?.radius?.regular ?? '8px'};
    outline: none;
    transition: ${theme?.component?.input?.transition ?? 'border-color 0.2s ease'};

    &::placeholder {
      color: ${tokens.placeholderColor ?? theme?.colors?.text?.tertiary ?? '#5a5a5a'};
    }

    &:focus {
      border-color: ${
        $hasError
          ? tokens.errorBorderColor ?? '#d32f2f'
          : tokens.focusBorderColor ?? theme?.colors?.text?.primary
      };
      ${tokens.focusRing ? `box-shadow: ${tokens.focusRing};` : ''}
    }

    &:disabled {
      opacity: ${tokens.disabledOpacity ?? 0.6};
      cursor: not-allowed;
    }
  `;
};

export const StyledInput = styled.input<FieldProps>`
  ${(props) => fieldBase(props)}
`;

export const StyledTextarea = styled.textarea<FieldProps & { $minHeight?: string | number }>`
  ${(props) => fieldBase(props)}
  resize: vertical;
  min-height: ${({ $minHeight, $size, theme }) => {
    if ($minHeight != null) {
      return typeof $minHeight === 'number' ? `${$minHeight}px` : $minHeight;
    }
    return getSizeTokens(theme, $size).minHeight ?? '100px';
  }};
  line-height: 1.5;
`;
